import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

/*
 * RescuePartnersStrip
 * ---------------------------------------------------------------------------
 * A compact homepage teaser that shows up to 3 featured rescue partners with
 * their logos and a "View All" CTA. Renders nothing when the API returns no
 * featured rescues so the homepage doesn't display an empty section.
 */
export default function RescuePartnersStrip() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/rescue-partners/featured", { params: { limit: 3 } })
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  if (loaded && items.length === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-6 lg:px-8 mt-12"
      data-testid="home-rescue-strip"
    >
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5 pb-3 border-b border-zinc-900">
        <div>
          <div className="label-eyebrow text-red-600 flex items-center gap-1.5">
            <HeartHandshake size={11} /> Rescue Partners
          </div>
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-1 leading-tight">
            Meet the organizations giving animals a second chance.
          </h2>
        </div>
        <Link
          to="/rescue-partners"
          className="btn-outline text-xs"
          data-testid="home-rescue-strip-viewall"
        >
          View All Rescue Partners <ArrowRight size={12} />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => (
          <Link
            key={r.id}
            to="/rescue-partners"
            className="border border-zinc-900 bg-white p-4 flex items-center gap-4 hover-shadow-harsh transition-shadow"
            data-testid={`home-rescue-strip-item-${r.id}`}
          >
            <div className="w-14 h-14 flex-shrink-0 border border-zinc-300 bg-zinc-50 flex items-center justify-center overflow-hidden">
              {r.logo_url ? (
                <img
                  src={r.logo_url}
                  alt={`${r.name} logo`}
                  className="w-full h-full object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <HeartHandshake size={22} className="text-zinc-400" />
              )}
            </div>
            <div className="min-w-0">
              <div className="label-eyebrow">{r.city}, {r.state_code || r.state}</div>
              <div className="font-display font-black text-sm leading-snug mt-0.5 truncate">
                {r.name}
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-1">
                {r.description}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
